import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import {
  Loader2, ChevronLeft, ThumbsUp, MessageCircle, Lightbulb, Archive,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Avatar } from "@/components/AppHeader";
import { timeAgo } from "@/components/Cards";

const STATUS_META = {
  proposed:     { ar: "مقترح",        he: "מוצע",       en: "Proposed",     color: "#6B7280", bg: "#E5E7EB" },
  under_review: { ar: "قيد الدراسة",  he: "בבדיקה",     en: "Under review", color: "#06B6D4", bg: "#CFFAFE" },
  approved:     { ar: "مُعتمَد",      he: "אושר",       en: "Approved",     color: "#673AB7", bg: "#EDE7F6" },
  in_progress:  { ar: "قيد التنفيذ",  he: "בביצוע",     en: "In progress",  color: "#F59E0B", bg: "#FEF3C7" },
  completed:    { ar: "مكتمل",        he: "הושלם",      en: "Completed",    color: "#10B981", bg: "#D1FAE5" },
};

export default function IdeaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useI18n();
  const [idea, setIdea] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const isMunicipality = user && (user.role === "admin" || user.role === "municipality_admin");
  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([
          api.get(`/ideas/${id}`),
          api.get(`/comments/idea/${id}`).catch(() => ({ data: [] })),
        ]);
        setIdea(r.data); setComments(c.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const toggleSupport = async () => {
    try {
      const { data } = await api.post(`/ideas/${id}/support`);
      setIdea((it) => ({ ...it, supported_by_me: data.supported, supporters_count: data.supporters_count }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/ideas/${id}`, { status });
      setIdea(data);
      toast.success(L("تم تحديث الحالة", "הסטטוס עודכן", "Status updated"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archive = async () => {
    if (!window.confirm(L("أرشف هذه الفكرة؟", "לארכב את הרעיון?", "Archive this idea?"))) return;
    try {
      await api.delete(`/ideas/${id}`);
      toast.success(L("تمت الأرشفة", "ארכוב הושלם", "Archived"));
      navigate("/ideas");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/comments/idea/${id}`, { content: newComment.trim() });
      setComments((cs) => [...cs, data]);
      setNewComment("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const topBar = (
    <div className="h-14 px-3 flex items-center gap-2">
      <button
        onClick={() => navigate(-1)}
        data-testid="idea-back-btn"
        className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <h1 className="font-extrabold text-[18px] text-[var(--te-text)] flex-1 truncate" dir="auto">
        {idea?.title || L("فكرة", "רעיון", "Idea")}
      </h1>
      {idea && user && (String(user.id) === String(idea.author?.id) || isMunicipality) && (
        <button
          onClick={archive}
          data-testid="idea-archive-btn"
          className="w-9 h-9 rounded-full bg-[var(--te-danger-soft)] hover:bg-[#FED7D7] flex items-center justify-center text-[var(--te-danger)]"
          title={L("أرشفة", "ארכוב", "Archive")}
        >
          <Archive size={15} />
        </button>
      )}
    </div>
  );

  if (loading) return <MobileShell topBar={topBar}><div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div></MobileShell>;
  if (!idea)    return <MobileShell topBar={topBar}><div className="text-center py-24 text-[var(--te-text-secondary)]">{L("الفكرة غير موجودة", "הרעיון לא נמצא", "Idea not found")}</div></MobileShell>;

  const meta = STATUS_META[idea.status] || STATUS_META.proposed;

  return (
    <MobileShell topBar={topBar}>
      <article className="te-fade-up">
        {idea.image_url && (
          <img src={idea.image_url} alt="" className="w-full h-44 object-cover" />
        )}
        <div className="px-4 pt-4 pb-6 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                  style={{ background: meta.bg, color: meta.color }}
                  data-testid="idea-status-badge">
              {meta[language] || meta.en}
            </span>
            {idea.category && (
              <span className="text-[11px] text-[var(--te-text-muted)] uppercase tracking-wider" dir="auto">
                {idea.category}
              </span>
            )}
          </div>

          <h2 className="font-extrabold text-[22px] text-[var(--te-text)] leading-tight" dir="auto">{idea.title}</h2>

          {idea.author && (
            <Link to={`/profile/${idea.author.id}`} className="flex items-center gap-2.5">
              <Avatar user={idea.author} size={36} />
              <div className="min-w-0">
                <div className="font-bold text-[14px] text-[var(--te-text)] truncate" dir="auto">{idea.author.name}</div>
                <div className="text-[11px] text-[var(--te-text-muted)]">{timeAgo(idea.created_at)}</div>
              </div>
            </Link>
          )}

          <p className="text-[15px] text-[var(--te-text)] leading-relaxed whitespace-pre-wrap" dir="auto">
            {idea.description}
          </p>

          {/* Stats row */}
          <div className="te-card p-4 flex items-center justify-between" data-testid="idea-stats">
            <div className="text-center flex-1">
              <div className="font-extrabold text-[22px] text-[var(--te-text)]" data-testid="idea-supporters-count">{idea.supporters_count}</div>
              <div className="text-[11px] text-[var(--te-text-muted)]">{L("داعمون", "תומכים", "supporters")}</div>
            </div>
            <div className="w-px h-10 bg-[var(--te-divider)]" />
            <div className="text-center flex-1">
              <div className="font-extrabold text-[22px] text-[var(--te-text)]">{comments.length}</div>
              <div className="text-[11px] text-[var(--te-text-muted)]">{L("تعليقات", "תגובות", "comments")}</div>
            </div>
          </div>

          <button
            onClick={toggleSupport}
            data-testid="idea-support-btn"
            className={`w-full !py-3 rounded-full font-semibold text-[14px] inline-flex items-center justify-center gap-1.5 border ${
              idea.supported_by_me
                ? "bg-[var(--te-primary)] text-white border-[var(--te-primary)]"
                : "bg-white text-[var(--te-text)] border-[var(--te-divider)] hover:bg-[var(--te-bg-soft)]"
            }`}
          >
            <ThumbsUp size={15} fill={idea.supported_by_me ? "currentColor" : "none"} />
            {idea.supported_by_me ? L("داعم", "תומך", "Supporting") : L("ادعم الفكرة", "תמוך ברעיון", "Support this idea")}
          </button>

          {/* Municipality status controls */}
          {isMunicipality && (
            <div className="te-card p-3.5" data-testid="idea-admin-controls">
              <div className="text-[11px] font-semibold text-[var(--te-text-secondary)] uppercase tracking-wider mb-2">
                {L("لوحة البلدية", "פאנל עירוני", "Municipality controls")}
              </div>
              <div className="flex flex-wrap gap-2">
                {["proposed", "under_review", "approved", "in_progress", "completed"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    data-testid={`idea-set-status-${s}`}
                    className={`text-[12px] font-semibold px-3 py-1.5 rounded-full border ${
                      idea.status === s
                        ? "bg-[var(--te-primary)] text-white border-[var(--te-primary)]"
                        : "bg-white text-[var(--te-text)] border-[var(--te-divider)]"
                    }`}
                  >
                    {STATUS_META[s][language] || STATUS_META[s].en}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <section className="mt-2">
            <h3 className="font-extrabold text-[16px] text-[var(--te-text)] mb-3">
              {L("تعليقات", "תגובות", "Comments")} ({comments.length})
            </h3>
            <form onSubmit={submitComment} className="te-card p-3 mb-3 flex gap-3">
              <Avatar user={user} size={36} />
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={L("شارك رأيك…", "שתף את דעתך…", "Share your thoughts…")}
                  className="te-input min-h-[60px] resize-y text-sm"
                  data-testid="idea-comment-input"
                  dir="auto"
                />
                <div className="flex justify-end mt-2">
                  <button type="submit" disabled={busy || !newComment.trim()} className="te-btn-primary text-[13px] !py-2 !px-4" data-testid="idea-comment-submit">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : null}
                    {L("نشر", "פרסם", "Post")}
                  </button>
                </div>
              </div>
            </form>

            {comments.length === 0 ? (
              <div className="text-center text-sm text-[var(--te-text-muted)] py-6">
                {L("كن أول من يعلّق.", "היה הראשון להגיב.", "Be the first to comment.")}
              </div>
            ) : (
              <div className="space-y-2.5">
                {comments.map((c) => (
                  <div key={c.id} className="te-card p-3 flex gap-3" data-testid={`idea-comment-${c.id}`}>
                    <Avatar user={c.author} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-[var(--te-text)]" dir="auto">{c.author?.name}</div>
                      <div className="text-[11px] text-[var(--te-text-muted)] mb-1">{timeAgo(c.created_at)}</div>
                      <p className="text-[13px] text-[var(--te-text)] whitespace-pre-wrap" dir="auto">{c.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </article>
    </MobileShell>
  );
}
